/**
 * The billing summary store — loads the account's tier/balance/subscription/
 * statement/catalog on demand, and drives the two PURCHASE hand-offs plus the
 * cancellation. Thin: the edge is the sole authority on every number and on what
 * a checkout costs; this only holds load/pending state and hands the browser to
 * the rail-hosted (CCBill) URL the edge mints.
 *
 * Nothing here grants credits or flips a tier — the verified webhook does that —
 * so after a cancel the store re-reads the summary rather than assuming a status.
 * The navigation seam is injected (DIP) so `bun test` exercises the whole
 * purchase path with zero real redirects.
 */
import { type ApiError, isErr, type Tier } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { BillingSummaryView } from '../api/types.ts';

/**
 * Hands the browser to a rail-hosted checkout URL (DIP). The default assigns
 * `location.href` — a full navigation, NOT a popup, so the discreet CCBill flow
 * owns the whole viewport and returns to the app afterwards.
 */
export type CheckoutNavigator = (url: string) => void;

/** The default navigator; a no-op outside a browser (SSR / `bun test`). */
export function browserCheckoutNavigator(): CheckoutNavigator {
  return (url) => {
    const location = globalThis.location;
    if (location) {
      location.href = url;
    }
  };
}

/** Which purchase action is in flight, or `null`. Drives per-button loading state. */
export type BillingPending = { readonly kind: 'plan' | 'pack' | 'cancel'; readonly id: string };

export interface BillingStore {
  readonly summary: () => BillingSummaryView | null;
  readonly loading: () => boolean;
  readonly error: () => ApiError | null;
  /** The purchase/cancel action currently in flight, or `null`. */
  readonly pending: () => BillingPending | null;
  /** Fetch (or re-fetch) the summary. */
  load(): Promise<void>;
  /** Start a subscription checkout for `tier` and hand the browser to the rail. */
  subscribe(tier: Tier): Promise<void>;
  /** Start a top-up checkout for `packId` and hand the browser to the rail. */
  buyPack(packId: string): Promise<void>;
  /** Cancel the active subscription, then re-read the authoritative summary. */
  cancelSubscription(): Promise<void>;
}

export function createBillingStore(
  api: ApiClient,
  navigate: CheckoutNavigator = browserCheckoutNavigator(),
): BillingStore {
  const [summary, setSummary] = createSignal<BillingSummaryView | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);
  const [pending, setPending] = createSignal<BillingPending | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getBillingSummary();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      setSummary(result.value);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Run one checkout hand-off under the shared pending/error discipline: refuse
   * to start a second while one is in flight (a double-tap must never open two
   * checkouts), surface the edge's `err` verbatim, and navigate only on `ok`.
   */
  const handoff = async (
    key: BillingPending,
    start: () => Promise<Awaited<ReturnType<ApiClient['startTopupCheckout']>>>,
  ): Promise<void> => {
    if (pending()) {
      return;
    }
    setPending(key);
    setError(null);
    try {
      const result = await start();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      navigate(result.value.checkoutUrl);
    } finally {
      setPending(null);
    }
  };

  return {
    summary,
    loading,
    error,
    pending,
    load,
    subscribe: (tier) =>
      handoff({ kind: 'plan', id: tier }, () => api.startSubscriptionCheckout(tier)),
    buyPack: (packId) =>
      handoff({ kind: 'pack', id: packId }, () => api.startTopupCheckout(packId)),
    async cancelSubscription(): Promise<void> {
      if (pending()) {
        return;
      }
      setPending({ kind: 'cancel', id: 'subscription' });
      setError(null);
      try {
        const result = await api.cancelSubscription();
        if (isErr(result)) {
          setError(result.error);
          return;
        }
      } finally {
        setPending(null);
      }
      // The edge is the authority on the resulting status — never assume it.
      await load();
    },
  };
}
